import type { Room, Vec2 } from "./types";
import { interiorSegments } from "./interiorWalls";
import {
  nearestPointOnSegment,
  pointInPolygon,
  polygonIntersectionArea,
  subtractPolygons,
  thickenedSegment,
  wallDirection,
} from "./geom";

/**
 * A bounded region of the plan a piece of furniture can live in. When a room
 * has no interior walls there is exactly one cell: the whole outline. Each
 * interior wall carves the outline into the sub-rooms it forms; a spur that
 * doesn't close a second region leaves one cell, notched by the spur so the
 * wall stays a solid obstacle.
 *
 * Cell polygons exclude the interior-wall thickness (the same slab
 * `clampPoseInRoom` keeps furniture clear of), so a footprint that sits
 * strictly inside a cell is already off every interior wall.
 */
export interface RoomCell {
  id: string;
  polygon: Vec2[];
}

/** Minimum interior-wall slab used to split cells, mirroring `clampPoseInRoom`. */
const MIN_WALL_SLAB_IN = 4;

/** An endpoint this close to an outline edge is treated as joined to it. */
const ON_BOUNDARY_EPS_IN = 2;

/** Overlap below this (square inches) is numerical noise, not containment. */
const MIN_OVERLAP_SQ_IN = 0.5;

function centroid(points: Vec2[]): Vec2 {
  if (points.length === 0) return { x: 0, y: 0 };
  let x = 0;
  let y = 0;
  for (const p of points) {
    x += p.x;
    y += p.y;
  }
  return { x: x / points.length, y: y / points.length };
}

function onBoundary(p: Vec2, outline: Vec2[]): boolean {
  const n = outline.length;
  for (let i = 0; i < n; i++) {
    const a = outline[i];
    const b = outline[(i + 1) % n];
    const foot = nearestPointOnSegment(p, a, b);
    const dx = foot.x - p.x;
    const dy = foot.y - p.y;
    if (dx * dx + dy * dy <= ON_BOUNDARY_EPS_IN * ON_BOUNDARY_EPS_IN) return true;
  }
  return false;
}

/**
 * A solid slab for one interior-wall segment. Ends that land on the outline
 * are extended outward so the slab punches fully through it and the cut
 * disconnects the two sides cleanly; a free spur end is left as-is so the
 * spur doesn't accidentally reach across and split the room.
 */
function wallSlab(a: Vec2, b: Vec2, thickness: number, outline: Vec2[]): Vec2[] {
  const dir = wallDirection(a, b);
  const ext = thickness;
  const a2 = onBoundary(a, outline)
    ? { x: a.x - dir.x * ext, y: a.y - dir.y * ext }
    : a;
  const b2 = onBoundary(b, outline)
    ? { x: b.x + dir.x * ext, y: b.y + dir.y * ext }
    : b;
  return thickenedSegment(a2, b2, thickness);
}

/**
 * Partition a room into the cells furniture can occupy. Always returns at
 * least one cell (the outline) when the outline is a valid polygon.
 */
export function computeRoomCells(room: Room): RoomCell[] {
  const outline = room.points;
  if (outline.length < 3) return [];
  const single: RoomCell[] = [
    { id: "cell:0", polygon: outline.map((p) => ({ ...p })) },
  ];

  const segs = interiorSegments(room).filter((s) => s.len > 0);
  if (segs.length === 0) return single;

  const thickness = Math.max(room.wallThickness || 0, MIN_WALL_SLAB_IN);
  const slabs = segs.map((s) => wallSlab(s.a, s.b, thickness, outline));
  const pieces = subtractPolygons(outline, slabs);
  if (pieces.length === 0) return single;

  return pieces
    .map((polygon) => ({ polygon, c: centroid(polygon) }))
    .sort((p, q) => p.c.x - q.c.x || p.c.y - q.c.y)
    .map((entry, i) => ({ id: `cell:${i}`, polygon: entry.polygon }));
}

/**
 * Decide which cell a world-space footprint belongs to. A footprint that
 * overlaps a cell with real area is locked to it (the larger overlap wins; on
 * a near-tie the cell containing the footprint's center). A footprint clear of
 * every cell returns `null` — it's free to be placed in any room.
 */
export function assignCell(
  footprintWorld: Vec2[],
  cells: RoomCell[]
): string | null {
  if (cells.length === 0 || footprintWorld.length < 3) return null;
  const overlaps = cells.map((cell) => ({
    cell,
    area: polygonIntersectionArea(footprintWorld, cell.polygon),
  }));
  overlaps.sort((p, q) => q.area - p.area);
  const best = overlaps[0];
  if (!best || best.area <= MIN_OVERLAP_SQ_IN) return null;

  const second = overlaps[1];
  if (second && best.area - second.area <= best.area * 0.01) {
    const center = centroid(footprintWorld);
    for (const { cell } of overlaps) {
      if (
        cell.polygon === best.cell.polygon ||
        cell.polygon === second.cell.polygon
      ) {
        if (pointInPolygon(center, cell.polygon)) return cell.id;
      }
    }
  }
  return best.cell.id;
}

/** Cell lookup by id, for validators that resolve a piece's locked room. */
export function cellById(
  cells: RoomCell[],
  id: string | null | undefined
): RoomCell | null {
  if (!id) return null;
  return cells.find((c) => c.id === id) ?? null;
}
